import React from 'react';
import { Col, Form, Input, Row, Select, Typography } from 'antd';
import UploadButton from '../../../../../components/common/UploadButton';

const { Title } = Typography;

const Step2FunctionalBrakeEvaluationForm = ({ fileList, setFileList }) => {
  const blockInvalidNumberKeys = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  };
  // Dropdown options for different field types
  const yesNoOptions = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' }
  ];

  const engineSmokeOptions = [
    { value: 'Black', label: 'Black' },
    { value: 'White', label: 'White' },
    { value: 'Blue', label: 'Blue' },
    { value: 'None', label: 'None' }
  ];

  const infotainmentOptions = [
    { value: 'Defect', label: 'Defect' },
    { value: 'Missing', label: 'Missing' },
    { value: 'Ok', label: 'Ok' }
  ];

  
  const wiperOptions = [
    { value: 'Front', label: 'Front' },
    { value: 'Rear', label: 'Rear' },
    { value: 'Both', label: 'Both' },
    { value: 'None', label: 'None' }
  ];

  const parkingSensorOptions = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
    { value: 'Unavailable', label: 'Unavailable' }
  ];

  // Render field with conditional cost and file - Optimized for compact layout
  const renderConditionalField = (fieldName, label, options, required = false, defaultValue = undefined) => (
    <Col xl={8} lg={12} md={24} sm={24} xs={24} className='mb-2'>
      <div style={{ border: '1px solid #f0f0f0', borderRadius: '6px', padding: '12px', backgroundColor: '#fafafa' }}>
        <Form.Item
          label={<span style={{ fontSize: '12px', fontWeight: '500' }}>{label}</span>}
          name={fieldName}
          rules={required ? [{ required: true, message: `Please select ${label}` }] : []}
          initialValue={defaultValue || (options.length === 2 && options[0].value === 'Yes' ? 'No' : undefined)}
          style={{ marginBottom: '8px' }}
        >
          <Select className='form-control-ant' placeholder='Select' size='small'>
            {options.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Conditional Cost and File Fields - Display below dropdown in same container */}
        <Form.Item dependencies={[fieldName]} noStyle>
          {({ getFieldValue }) => {
            const fieldValue = getFieldValue(fieldName);
            let shouldShow = false;

            // Special conditions for different fields based on specifications
            if (fieldName === 'engine_smoke') {
              // Displayed field value is selected except None
              shouldShow = fieldValue && fieldValue !== 'None';
            } else if (fieldName === 'infotainment_system_defect') {
              // Display field if selected other than ok
              shouldShow = fieldValue && fieldValue !== 'Ok';
            } else if (fieldName === 'wiper_defect') {
              // Display field if value other than "None" is selected
              shouldShow = fieldValue && fieldValue !== 'None';
            } else {
              // Default: display filed if selected yes
              shouldShow = fieldValue === 'Yes';
            }

            return shouldShow ? (
              <Row gutter={[8, 0]}>
                <Col span={12}>
                  <Form.Item
                    label={<span style={{ fontSize: '11px' }}>Cost</span>}
                    name={`${fieldName}_cost`}
                    initialValue={0}
                    rules={[
                      {
                        required: true,
                        message: ``,
                      },
                      () => ({
                        validator(_, value) {
                          if (value === '' || value === null || value === undefined) {
                            return Promise.reject(`Please input ${label.toLowerCase()} cost`);
                          }
                          if (!/^\d{1,10}(\.\d{1,2})?$/.test(value)) {
                            return Promise.reject('Please enter a valid cost');
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                    style={{ marginBottom: '0' }}
                  >
                    <Input placeholder='Cost' className='form-control-ant' type='number' size='small' onKeyDown={blockInvalidNumberKeys} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={<span style={{ fontSize: '11px' }}>File</span>}
                    name={`${fieldName}_file`}
                    rules={[
                      {
                        required: true,
                        message: `Please upload ${label.toLowerCase()} file`,
                        validator: () => {
                          // Check if file exists in fileList
                          const fileFieldName = `${fieldName}_file`;
                          if (fileList[fileFieldName] && fileList[fileFieldName].length > 0) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(`Please upload ${label.toLowerCase()} file`));
                        }
                      },
                      
                    ]}
                    style={{ marginBottom: '0' }}
                  >
                    <UploadButton 
                      name={`${fieldName}_file`} 
                      fileList={fileList} 
                      setFileList={setFileList} 
                      fileType={['image/jpeg', 'image/png']} 
                      fileSizeLimit={5} 
                      documentType={`${fieldName}_file`} 
                      hideDeleteIcon={true}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ) : null;
          }}
        </Form.Item>
      </div>
    </Col>
  );

  return (
    <div>
      <Title level={4} className='mb-3'>Functional and Brake Evaluation Details</Title>
      
      {/* Engine Related Fields */}
      <Title level={5} className='mb-3'>Engine Related</Title>
      <Row gutter={[16, 16]}>
        {renderConditionalField('engine_oil_leakage', 'Engine Oil Leakage', yesNoOptions, true)}
        {renderConditionalField('coolant_leakage', 'Coolant Leakage', yesNoOptions, false)}
        {renderConditionalField('radiator_damage', 'Radiator Damage', yesNoOptions, false)}
        {renderConditionalField('starter_motor_defect', 'Starter Motor Defect', yesNoOptions, false)}
        {renderConditionalField('engine_noise', 'Engine Noise', yesNoOptions, true)}
        {renderConditionalField('low_pickup', 'Low Pickup', yesNoOptions, true)}
        {renderConditionalField('engine_smoke', 'Engine Smoke', engineSmokeOptions, true, 'None')}
        {renderConditionalField('defective_belts', 'Defective Belts', yesNoOptions, false)}
        {renderConditionalField('engine_replaced_endorsed', 'Engine Replaced Endorsed', yesNoOptions, true)}
        {renderConditionalField('rough_engine', 'Rough Engine', yesNoOptions, true)}
        {renderConditionalField('engine_rpm_defect', 'Engine RPM Defect', yesNoOptions, true)}
      </Row>
      
      <div className='h-line'>&nbsp;</div>

      {/* Brake Related Fields */}
      <Title level={5} className='mb-3'>Brake Related</Title>
      <Row gutter={[16, 16]}>
        {renderConditionalField('brake_disk_damage', 'Brake Disk Damage', yesNoOptions, false)}
        {renderConditionalField('brake_defect', 'Brake Defect', yesNoOptions, true)}
        {renderConditionalField('abs_ebd_defect', 'ABS/EBD Defect', yesNoOptions, false)}
      </Row>
      
      <div className='h-line'>&nbsp;</div>

      {/* Electrical and Electronic Systems */}
      <Title level={5} className='mb-3'>Electrical and Electronic Systems</Title>
      <Row gutter={[16, 16]}>
        {renderConditionalField('keyless_cylinder_defect', 'Keyless Cylinder Defect', yesNoOptions, true)}
        {renderConditionalField('warning_lamp_defect', 'Warning Lamp Defect', yesNoOptions, true)}
        {renderConditionalField('infotainment_system_defect', 'Infotainment System Defect', infotainmentOptions, true, 'Ok')}
        {renderConditionalField('head_lamp_defect', 'Head Lamp Defect', yesNoOptions, true)}
        {renderConditionalField('fog_lamp_defect', 'Fog Lamp Defect', yesNoOptions, true)}
        {renderConditionalField('horn_defect', 'Horn Defect', yesNoOptions, true)}
        {renderConditionalField('wiper_defect', 'Wiper Operation Defect', wiperOptions, true, 'None')}
        {renderConditionalField('navigation_system_defect', 'Navigation System Defect', yesNoOptions, true)}
        {renderConditionalField('analog_music_system_defect', 'Analog Music System Defect', yesNoOptions, true)}
        {renderConditionalField('cruise_control_defect', 'Cruise Control Defect', yesNoOptions, true)}
        {renderConditionalField('steering_wheel_control_defect', 'Steering Wheel Control Defect', yesNoOptions, true)}
        {renderConditionalField('electronic_mirror_defect', 'Electronic Mirror Defect', yesNoOptions, true)}
        {renderConditionalField('battery_defect', 'Battery Defect', yesNoOptions, true)}
        {renderConditionalField('music_system_defect', 'Music System Defect', yesNoOptions, true)}
        {renderConditionalField('power_window_defect', 'Power Window Defect', yesNoOptions, true)}
        {renderConditionalField('auto_headlight_wiper_defect', 'Auto Headlight Wiper Defect', yesNoOptions, true)}
        {renderConditionalField('electronic_tailgate_defect', 'Electronic TailGate Defect', yesNoOptions, true)}
        {renderConditionalField('parking_sensor_defect', 'Parking Sensor Defect', parkingSensorOptions, true, 'No')}
      </Row>
      
      <div className='h-line'>&nbsp;</div>

      {/* AC and Climate Control */}
      <Title level={5} className='mb-3'>AC and Climate Control</Title>
      <Row gutter={[16, 16]}>
        {renderConditionalField('ac_defective', 'AC Defective', yesNoOptions, true)}
        {renderConditionalField('roof_ac', 'Roof AC', yesNoOptions, true)}
        {renderConditionalField('ventilated_seat_defect', 'Ventilated Seat Defect', yesNoOptions, true)}
        {renderConditionalField('electronic_seat_defect', 'Electronic Seat Defect', yesNoOptions, true)}
      </Row>
      
      <div className='h-line'>&nbsp;</div>

      {/* Advanced Features */}
      <Title level={5} className='mb-3'>Advanced Features</Title>
      <Row gutter={[16, 16]}>
        {renderConditionalField('paddle_shift_defect', 'Paddle Shift Defect', yesNoOptions, true)}
        {renderConditionalField('tyre_pressuring_monitoring_system_defect', 'Tyre Pressuring Monitoring System Defect', yesNoOptions, true)}
        {renderConditionalField('sun_roof_defect', 'Sun Roof Defect', yesNoOptions, true)}
        {renderConditionalField('rear_view_mirror_irvm', 'Rear View Mirror IRVM', yesNoOptions, true)}
        {renderConditionalField('rear_view_mirror_orvm', 'Rear View Mirror ORVM', yesNoOptions, true)}
      </Row>
    </div>
  );
};

export default Step2FunctionalBrakeEvaluationForm;
